import { askAboutAndReveal } from "../../../state/chatActions";
import type { IntelEdge, MerchantSummary } from "../../../data/types";
import styles from "./IntelGraph.module.css";

interface IntelGraphProps {
  intel: IntelEdge[];
  merchants: MerchantSummary[];
}

export function IntelGraph({ intel, merchants }: IntelGraphProps) {
  const byId = new Map(merchants.map((m) => [m.id, m]));
  const byKind = new Map<string, IntelEdge[]>();
  intel.forEach((e) => {
    const arr = byKind.get(e.kind) ?? [];
    arr.push(e);
    byKind.set(e.kind, arr);
  });

  const kinds: { id: IntelEdge["kind"]; label: string }[] = [
    { id: "refund-cluster", label: "refund clusters" },
    { id: "shared-ip-range", label: "shared infra" },
    { id: "shared-bin", label: "shared bin" },
  ];

  return (
    <div className={styles.card}>
      <header className={styles.header}>
        <span className={styles.title}>cross-merchant intel</span>
        <span className={styles.muted}>{intel.length} edges in portfolio graph</span>
      </header>

      {kinds.map((kind) => {
        const edges = byKind.get(kind.id) ?? [];
        if (edges.length === 0) return null;
        return (
          <div key={kind.id} className={styles.group}>
            <button
              type="button"
              className={styles.groupLabel}
              onClick={() =>
                askAboutAndReveal(
                  `explain the ${kind.label} in the portfolio — what do these edges mean, which merchants cluster together, and what does that tell us about shared risk?`
                )
              }
            >
              {kind.label}
            </button>
            <ul className={styles.edges}>
              {edges.slice(0, 6).map((e, i) => {
                const src = byId.get(e.source);
                const tgt = byId.get(e.target);
                const srcName = src?.name ?? e.source;
                const tgtName = tgt?.name ?? e.target;
                return (
                  <li key={i}>
                    <button
                      type="button"
                      className={styles.edge}
                      onClick={() =>
                        askAboutAndReveal(
                          `explain the ${kind.label.replace(/s$/, "")} link between ${srcName} and ${tgtName} (strength ${(e.strength * 100).toFixed(0)}%): ${e.note}. how strong is the correlation and should we treat them as a cluster for intervention purposes?`
                        )
                      }
                      title="drill into this cluster"
                    >
                      <span className={styles.merchant}>{srcName}</span>
                      <span className={styles.link} aria-hidden>
                        ↔
                      </span>
                      <span className={styles.merchant}>{tgtName}</span>
                      <span className={styles.strength}>
                        {(e.strength * 100).toFixed(0)}%
                      </span>
                      <span className={styles.note}>{e.note}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </div>
  );
}
